<template>
  <div class="tasks-layout text-dark">
    <div class="tasks-header">
      <div class="tasks-header__left">
        <DoFiltersFilter
          :title="i18n[locale]['task_names']"
          :tags="taskNameLabels"
          :selectedTags="selectedFilterTaskNames"
          @select="toggleFilterTaskNames"
          @clear="selectedFilterTaskNames = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="i18n[locale]['brick_type']"
          :tags="filterLabels"
          :selectedTags="selectedFilterSubjectTypes"
          @select="toggleFilterSubjectTypes"
          @clear="selectedFilterSubjectTypes = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="i18n[locale]['tasks_status']"
          :tags="taskStatusFilters"
          :selectedTags="selectedFilterTaskStatus"
          @select="toggleFilterTaskStatus"
          @clear="selectedFilterTaskStatus = []">
        </DoFiltersFilter>
      </div>
      <div class="tasks-header__right">
        <div class="do-spinner" v-if="isSubmittingTask"></div>
        <button
          class="do-bttn do-bttn--menu-button"
          content="Download til excel"
          v-tippy="{ arrow: true, placement: 'bottom' }"
          @click="asyncLoadXLSXAndDownload"
          :disabled="isDownloading || !activeGroupData"
        >
          <span v-if="isDownloading" class="do-spinner do-spinner--small"></span>
          <span v-else class="icon-download"></span>
        </button>
        <DDropdown :isShowDots="false" :isBottomStart="false" :isCloseOnDropdownClick="true" :propOffset="[0, 8]">
          <template #title>
            <button class="do-bttn do-bttn--menu-button"
                    :class="{'do-bttn--active': bulkActionMode}">
              {{ i18n[locale]['bulk_actions'] }} <span class="icon-menu-open"></span>
            </button>
          </template>
          <template #list>
            <ul class="do-bttn--menu-button__list">
              <li @click="startBulkAction('subject_type')">
                {{ i18n[locale]['change_type'] }}
                <span v-if="bulkActionMode && bulkActionType === 'subject_type'" class="icon-checkmark-circle"></span>
              </li>
              <li @click="startBulkAction('hours')">
                {{ i18n[locale]['change_hours'] }}
                <span v-if="bulkActionMode && bulkActionType === 'hours'" class="icon-checkmark-circle"></span>
              </li>
              <li @click="startBulkAction('dates')">
                {{ i18n[locale]['change_dates'] }}
                <span v-if="bulkActionMode && bulkActionType === 'dates'" class="icon-checkmark-circle"></span>
              </li>
              <li @click="startBulkAction('delete')">
                {{ i18n[locale]['delete_subjects'] }}
                <span v-if="bulkActionMode && bulkActionType === 'delete'" class="icon-checkmark-circle"></span>
              </li>
            </ul>
          </template>
        </DDropdown>
        <DDropdown :isShowDots="false" :isBottomStart="false" :isCloseOnDropdownClick="false" :propOffset="[0, 8]">
          <template #title>
            <button class="do-bttn do-bttn--menu-button">{{ i18n[locale]['view'] }} <span class="icon-menu-open"></span></button>
          </template>
          <template #list>
            <ul class="do-bttn--menu-button__list">
              <li @click="showCalendarInTable = !showCalendarInTable">
                {{ i18n[locale]['show_calendar_in_table'] }}
                <span v-if="showCalendarInTable" class="icon-checkmark-circle"></span>
              </li>
              <li @click="showDatesInTable = !showDatesInTable">
                {{ i18n[locale]['show_dates_in_table'] }}
                <span v-if="showDatesInTable" class="icon-checkmark-circle"></span>
              </li>
              <li @click="showUsersInTable = !showUsersInTable">
                {{ i18n[locale]['show_users_in_table'] }}
                <span v-if="showUsersInTable" class="icon-checkmark-circle"></span>
              </li>
              <li @click="showTotalInTable = !showTotalInTable">
                {{ i18n[locale]['show_total_in_table'] }}
                <span v-if="showTotalInTable" class="icon-checkmark-circle"></span>
              </li>
              <li @click="unmergeTasks = !unmergeTasks">
                {{ i18n[locale]['unmerge_tasks'] }}
                <span v-if="unmergeTasks" class="icon-checkmark-circle"></span>
              </li>
            </ul>
          </template>
        </DDropdown>
        <button class="do-bttn do-bttn--submit" @click="openCreateTask">
          {{ i18n[locale]['create_tasks'] }}
        </button>
      </div>
    </div>
    <div class="tasks-body">
    <div class="tasks-sidebar" :class="{'tasks-sidebar--collapsed': sidebarCollapsed}">
      <div class="sidebar-header">
        <span v-if="!sidebarCollapsed">{{ i18n[locale]['tasks_label'] }} ({{ filteredGroupedTasks.length }} {{ i18n[locale]['of'] }} {{ totalGroupCount }})</span>
        <button class="do-bttn do-bttn--submit sidebar-create-btn" v-if="!sidebarCollapsed" @click="openCreateTask">+</button>
        <button class="sidebar-collapse-btn" @click="sidebarCollapsed = !sidebarCollapsed">
          <span :class="sidebarCollapsed ? 'icon-arrow-right4' : 'icon-arrow-left4'"></span>
        </button>
      </div>
      <template v-if="!sidebarCollapsed">
        <ul class="sidebar-list" v-if="filteredGroupedTasks.length > 0">
          <li class="sidebar-item"
              :class="{'sidebar-item--active': activeGroupName === '__all__'}"
              @click="setActiveGroup('__all__')">
            <span class="sidebar-name">{{ i18n[locale]['all'] }}</span>
            <span class="sidebar-count">{{ Math.round(allFilteredWeightedHours * 10) / 10 }} {{ i18n[locale]['hours_short'] }}</span>
          </li>
          <li v-for="group in filteredGroupedTasks"
              :key="group.name"
              class="sidebar-item"
              :class="{'sidebar-item--active': activeGroupName === group.name}"
              @click="setActiveGroup(group.name)">
            <span class="sidebar-name">{{ group.name }}</span>
            <span class="sidebar-count">{{ Math.round(group.weightedHours * 10) / 10 }} {{ i18n[locale]['hours_short'] }}</span>
          </li>
        </ul>
        <div v-else class="sidebar-empty">{{ i18n[locale]['no_tasks'] }}</div>
      </template>
    </div>
    <div class="tasks-main">
      <template v-if="activeGroupData">
        <div v-if="bulkActionMode" class="bulk-action-bar">
          <button class="do-bttn" @click="toggleSelectAllVisible">
            {{ allVisibleSelected ? i18n[locale]['deselect_all'] : i18n[locale]['select_all_visible'] }}
          </button>
          <span class="bulk-action-bar__count">{{ selectedRowIds.length }} {{ i18n[locale]['selected'] }}</span>
          <template v-if="selectedRowIds.length > 0 && bulkActionType === 'subject_type'">
            <label class="bulk-action-bar__label">{{ i18n[locale]['change_type'] }}:</label>
            <select v-model="bulkSubjectType" class="do-select d-input bulk-action-bar__select">
              <option value="" disabled>{{ i18n[locale]['choose_type_placeholder'] }}</option>
              <option v-for="opt in bulkSubjectTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
            <button class="do-bttn do-bttn--submit"
                    :disabled="!bulkSubjectType || isApplyingBulk"
                    :class="{'do-bttn--submit--loading': isApplyingBulk}"
                    @click="applyBulkSubjectType">
              {{ i18n[locale]['apply'] }}
            </button>
          </template>
          <template v-if="selectedRowIds.length > 0 && bulkActionType === 'hours'">
            <label class="bulk-action-bar__label">{{ i18n[locale]['change_hours'] }}:</label>
            <input type="number"
                   class="d-input bulk-action-bar__input"
                   v-model.number="bulkHours"
                   :placeholder="i18n[locale]['hours_placeholder']"
                   min="0"
                   step="0.25">
            <button class="do-bttn do-bttn--submit"
                    :disabled="bulkHours == null || bulkHours === '' || isApplyingBulk"
                    :class="{'do-bttn--submit--loading': isApplyingBulk}"
                    @click="applyBulkHours">
              {{ i18n[locale]['apply'] }}
            </button>
          </template>
          <template v-if="selectedRowIds.length > 0 && bulkActionType === 'dates'">
            <label class="bulk-action-bar__label">{{ i18n[locale]['change_dates'] }}:</label>
            <DatePickerRange
              :startdate="bulkFromDate"
              :enddate="bulkToDate"
              :minDate="bulkDateMin"
              :maxDate="bulkDateMax"
              :maxEndDate="bulkDateMax"
              :allowSameDate="true"
              @startchanged="setBulkFromDate"
              @endchanged="setBulkToDate">
            </DatePickerRange>
            <button class="do-bttn do-bttn--submit"
                    :disabled="!bulkFromDate || !bulkToDate || isApplyingBulk"
                    :class="{'do-bttn--submit--loading': isApplyingBulk}"
                    @click="applyBulkDates">
              {{ i18n[locale]['apply'] }}
            </button>
          </template>
          <template v-if="selectedRowIds.length > 0 && bulkActionType === 'delete'">
            <span v-if="skippedSubjectCount > 0" class="bulk-action-bar__warning">
              {{ skippedSubjectCount }} {{ i18n[locale]['bulk_delete_skipped'] }}
            </span>
            <button class="do-bttn do-bttn--delete"
                    :disabled="deletableSubjectIds.length === 0 || isApplyingBulk"
                    :class="{'do-bttn--submit--loading': isApplyingBulk}"
                    @click="applyBulkDelete">
              {{ i18n[locale]['delete'] }} ({{ deletableSubjectIds.length }})
            </button>
          </template>
          <button class="do-bttn bulk-action-bar__cancel" @click="cancelBulkAction">
            {{ i18n[locale]['cancel'] }}
          </button>
        </div>
        <div class="task-table-wrap">
          <table class="d-table">
            <thead>
              <tr>
                <th v-if="bulkActionMode" class="task-table-checkbox" style="width: 36px">
                  <input type="checkbox"
                         :checked="allVisibleSelected"
                         @click.stop="toggleSelectAllVisible">
                </th>
                <th style="width: 220px"></th>
                <th class="d-table__number" style="width: 80px">{{ i18n[locale]['hours'] }}</th>
                <th style="width: 120px">{{ i18n[locale]['type'] }}</th>
                <th v-if="showCalendarInTable" style="width: 150px">{{ i18n[locale]['calendar'] }}</th>
                <th v-if="showDatesInTable" style="width: 180px">{{ i18n[locale]['dates'] }}</th>
                <th v-if="showUsersInTable" style="min-width: 250px"><span class="icon-user3"></span> {{ i18n[locale]['users'] }}</th>
                <th v-if="showTotalInTable" class="d-table__number" style="width: 80px">{{ i18n[locale]['total'] }}</th>
                <th style="width: 50px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="activeGroupName !== '__all__'" class="task-table-row task-table-row--add" @click="duplicateFromGroup">
                <td :colspan="visibleColumnCount">{{ i18n[locale]['new_task'] }}</td>
              </tr>
              <tr v-for="task in activeGroupData.tasks" :key="task._generatedId || task.id">
                <td v-if="bulkActionMode" class="task-table-checkbox">
                  <input type="checkbox"
                         :checked="isRowSelected(task)"
                         @click.stop="toggleRowSelection(task)">
                </td>
                <td>
                  <div class="task__name">
                    <div @click="setEditTask(task)"
                         :class="[{'td-subject--hidden': !task.can_schedule}, {'td-subject': task.can_schedule}]"
                         :style="{'background-color': task.color}">
                      {{ task.name || '' }}
                    </div>
                  </div>
                </td>
                <td class="d-table__number">{{ task.hours ? parseFloat(task.hours.toFixed(2)) : 0 }}</td>
                <td class="task-table-type">{{ getTaskTypeLabel(task) }}</td>
                <td v-if="showCalendarInTable"
                    class="task-table-calendar"
                    :class="{'task-table-calendar--own': task.in_user_calendar}">
                  {{ getTaskCalendarLabel(task) }}
                </td>
                <td v-if="showDatesInTable" class="task-table-dates">
                  {{ getTaskDatesLabel(task) }}
                </td>
                <td v-if="showUsersInTable"
                    @click="setActiveDSelect(task)"
                    :class="{'d-select-lookalike': activeDSelect !== (task._generatedId || task.id) && (task.user_calendar_ids || []).length < 1}">
                  <DSelectCalendarIds v-if="activeDSelect === (task._generatedId || task.id)"
                                      :show="activeDSelectShow"
                                      v-on-clickaway="closeDSelect"
                                      itemType="USER"
                                      :preSelectedItems="task.user_calendar_ids || []"
                                      :itemsThatCantBeDeleted="getUsersWithEvents(task)"
                                      :showTags="true"
                                      :sortSelectedByName="true"
                                      @update="setTableSelectedUsers($event, task)">
                  </DSelectCalendarIds>
                  <div class="task__users" v-if="activeDSelect !== (task._generatedId || task.id) && (task.user_calendar_ids || []).length > 0">
                    <p v-for="name in getUserNames(task.user_calendar_ids || [])" :key="name">{{ name }}</p>
                  </div>
                </td>
                <td v-if="showTotalInTable" class="d-table__number">{{ getTaskWeightedTotal(task) }}</td>
                <td class="task-table-actions">
                  <button class="task-table-copy" @click.stop="duplicateTask(task)" :title="i18n[locale]['create_copy']">+</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <div v-else class="main-empty">{{ i18n[locale]['select_task'] }}</div>
    </div>
    </div>
    <CreateTask
      v-if="isTaskModal"
      :editTask="editTask"
      :activeFilter="null"
      :prefill="prefillTask"
      :quickCreate="isQuickCreate"
      :isLoadingFreshData="isLoadingFreshTask"
      @close="closeCreateTask"
      @create="createTask"
    ></CreateTask>
  </div>
</template>

<script>
import CreateTask from './SubjectsDistributionTasksFromSubjectsCreate.vue'
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import DDropdown from '../common/DDropdown.vue'
import DatePickerRange from '../datepicker/DatePickerRange.vue'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import moment from 'moment'
import {allTypes as subjectAllTypes, kindergartenTypes as subjectKindergartenTypes} from '../Subjects/subjectTypes'
import {mixin as clickaway} from 'vue-clickaway'

let XLSX = null

const I18N = {
  da: {
    create_tasks: 'Opret opgave',
    teaching: 'Undervisning',
    additional_tasks: 'Øvrige opgaver',
    preparation: 'Forberedelse',
    pool: 'Lokalpulje',
    club: 'SFO',
    available: 'Rådighedstimer',
    task_names: 'Opgavens titel',
    brick_type: 'Type',
    tasks_label: 'Opgaver',
    of: 'af',
    all: 'Alle',
    hours_short: 't',
    no_tasks: 'Ingen opgaver',
    hours: 'Timer',
    type: 'Type',
    users: 'Brugere',
    total: 'Total',
    calendar: 'Kalender',
    own_calendar: 'Egen',
    new_task: '+ Ny opgave',
    create_copy: 'Opret kopi',
    select_task: 'Vælg en opgave i listen',
    task: 'Opgave',
    tasks_sheet: 'Opgaver',
    tasks_status: 'Status',
    only_tasks_with_users: 'Vis opgaver med personale',
    only_tasks_without_users: 'Vis opgaver uden personale',
    view: 'Visning',
    show_users_in_table: 'Vis brugere i tabellen',
    show_calendar_in_table: 'Vis kalender i tabellen',
    show_total_in_table: 'Vis total i tabellen',
    show_dates_in_table: 'Vis datoer i tabellen',
    unmerge_tasks: 'Vis én linje pr. brik',
    dates: 'Datoer',
    bulk_actions: 'Massehandling',
    select_all_visible: 'Vælg alle synlige',
    deselect_all: 'Fravælg alle',
    selected: 'valgt',
    cancel: 'Annullér',
    apply: 'Anvend',
    change_type: 'Skift briktype',
    change_hours: 'Skift timetal',
    change_dates: 'Skift datoer',
    delete_subjects: 'Slet brikker',
    delete: 'Slet',
    choose_type_placeholder: 'Vælg ny type …',
    hours_placeholder: 'Nyt timetal',
    bulk_subject_type_updated: 'Briktype opdateret',
    bulk_subject_type_failed: 'Kunne ikke opdatere briktype',
    bulk_hours_updated: 'Timetal opdateret',
    bulk_hours_failed: 'Kunne ikke opdatere timetal',
    bulk_dates_updated: 'Datoer opdateret',
    bulk_dates_failed: 'Kunne ikke opdatere datoer',
    bulk_delete_success: 'Brikker slettet',
    bulk_delete_failed: 'Kunne ikke slette brikker',
    bulk_delete_skipped: 'brikker kunne ikke slettes pga. skemalagte begivenheder',
    bulk_delete_all_skipped: 'Alle valgte brikker har skemalagte begivenheder og kan ikke slettes',
    bulk_delete_confirm: 'Er du sikker på, at du vil slette de valgte brikker?',
    bulk_dates_out_of_range: 'Datoer skal være inden for skoleåret'
  },
  nb: {
    create_tasks: 'Opprett oppgaver',
    teaching: 'Undervisning',
    additional_tasks: 'Øvrige oppgaver',
    preparation: 'Forberedelse',
    pool: 'Planleggingsdager',
    club: 'SFO',
    available: 'Rådighetstimer',
    task_names: 'Oppgave',
    brick_type: 'Type',
    tasks_label: 'Oppgaver',
    of: 'av',
    all: 'Alle',
    hours_short: 't',
    no_tasks: 'Ingen oppgaver',
    hours: 'Timer',
    type: 'Type',
    users: 'Brukere',
    total: 'Totalt',
    calendar: 'Kalender',
    own_calendar: 'Egen',
    new_task: '+ Ny oppgave',
    create_copy: 'Opprett kopi',
    select_task: 'Velg en oppgave i listen',
    task: 'Oppgave',
    tasks_sheet: 'Oppgaver',
    tasks_status: 'Status',
    only_tasks_with_users: 'Vis oppgaver med personale',
    only_tasks_without_users: 'Vis oppgaver uten personale',
    view: 'Visning',
    show_users_in_table: 'Vis brukere i tabellen',
    show_calendar_in_table: 'Vis kalender i tabellen',
    show_total_in_table: 'Vis totalt i tabellen',
    show_dates_in_table: 'Vis datoer i tabellen',
    unmerge_tasks: 'Vis én linje per brikk',
    dates: 'Datoer',
    bulk_actions: 'Massehandling',
    select_all_visible: 'Velg alle synlige',
    deselect_all: 'Fravelg alle',
    selected: 'valgt',
    cancel: 'Avbryt',
    apply: 'Bruk',
    change_type: 'Endre brikktype',
    change_hours: 'Endre timetall',
    change_dates: 'Endre datoer',
    delete_subjects: 'Slett brikker',
    delete: 'Slett',
    choose_type_placeholder: 'Velg ny type …',
    hours_placeholder: 'Nytt timetall',
    bulk_subject_type_updated: 'Brikktype oppdatert',
    bulk_subject_type_failed: 'Kunne ikke oppdatere brikktype',
    bulk_hours_updated: 'Timetall oppdatert',
    bulk_hours_failed: 'Kunne ikke oppdatere timetall',
    bulk_dates_updated: 'Datoer oppdatert',
    bulk_dates_failed: 'Kunne ikke oppdatere datoer',
    bulk_delete_success: 'Brikker slettet',
    bulk_delete_failed: 'Kunne ikke slette brikker',
    bulk_delete_skipped: 'brikker kunne ikke slettes pga. skemalagte hendelser',
    bulk_delete_all_skipped: 'Alle valgte brikker har skemalagte hendelser og kan ikke slettes',
    bulk_delete_confirm: 'Er du sikker på at du vil slette de valgte brikkene?',
    bulk_dates_out_of_range: 'Datoer må være innenfor skoleåret'
  },
  en: {
    create_tasks: 'Create task',
    teaching: 'Teaching',
    additional_tasks: 'Additional tasks',
    preparation: 'Preparation',
    pool: 'Custom pool',
    club: 'Club',
    available: 'Available hours',
    task_names: 'Task',
    brick_type: 'Type',
    tasks_label: 'Tasks',
    of: 'of',
    all: 'All',
    hours_short: 'h',
    no_tasks: 'No tasks',
    hours: 'Hours',
    type: 'Type',
    users: 'Users',
    total: 'Total',
    calendar: 'Calendar',
    own_calendar: 'Own',
    new_task: '+ New task',
    create_copy: 'Create copy',
    select_task: 'Select a task from the list',
    task: 'Task',
    tasks_sheet: 'Tasks',
    tasks_status: 'Status',
    only_tasks_with_users: 'Show tasks with personnel',
    only_tasks_without_users: 'Show tasks without personnel',
    view: 'View',
    show_users_in_table: 'Show users in table',
    show_calendar_in_table: 'Show calendar in table',
    show_total_in_table: 'Show total in table',
    show_dates_in_table: 'Show dates in table',
    unmerge_tasks: 'Show one row per tile',
    dates: 'Dates',
    bulk_actions: 'Bulk actions',
    select_all_visible: 'Select all visible',
    deselect_all: 'Deselect all',
    selected: 'selected',
    cancel: 'Cancel',
    apply: 'Apply',
    change_type: 'Change tile type',
    change_hours: 'Change hours',
    change_dates: 'Change dates',
    delete_subjects: 'Delete tiles',
    delete: 'Delete',
    choose_type_placeholder: 'Choose new type …',
    hours_placeholder: 'New hours',
    bulk_subject_type_updated: 'Tile type updated',
    bulk_subject_type_failed: 'Failed to update tile type',
    bulk_hours_updated: 'Hours updated',
    bulk_hours_failed: 'Failed to update hours',
    bulk_dates_updated: 'Dates updated',
    bulk_dates_failed: 'Failed to update dates',
    bulk_delete_success: 'Tiles deleted',
    bulk_delete_failed: 'Failed to delete tiles',
    bulk_delete_skipped: 'tiles could not be deleted due to scheduled events',
    bulk_delete_all_skipped: 'All selected tiles have scheduled events and cannot be deleted',
    bulk_delete_confirm: 'Are you sure you want to delete the selected tiles?',
    bulk_dates_out_of_range: 'Dates must be within the school year'
  }
}

const TASK_ALWAYS_TYPES = ['club', 'pool', 'person_resource', 'preparation', 'additional_tasks', 'with_kids', 'without_kids']
const TASK_TEACHING_TYPES = ['subject', 'person_resource_visible', 'with_kids', 'without_kids', 'available']

export default {
  mixins: [clickaway],
  components: { CreateTask, DSelectCalendarIds, DoFiltersFilter, DDropdown, DatePickerRange },
  data () {
    return {
      locale: window.USER && window.USER.language ? window.USER.language : 'da',
      activeGroup: null,
      isTaskModal: false,
      editTask: null,
      isLoadingFreshTask: false,
      prefillTask: null,
      isQuickCreate: false,
      activeDSelect: null,
      activeDSelectShow: false,
      selectedFilterSubjectTypes: [],
      selectedFilterTaskNames: [],
      selectedFilterTaskStatus: [],
      isDownloading: false,
      sidebarCollapsed: false,
      showUsersInTable: true,
      showCalendarInTable: true,
      showTotalInTable: true,
      showDatesInTable: false,
      unmergeTasks: false,
      bulkActionMode: false,
      bulkActionType: '',
      selectedRowIds: [],
      bulkSubjectType: '',
      bulkHours: null,
      bulkFromDate: null,
      bulkToDate: null,
      isApplyingBulk: false,
      calSubjectTypes: []
    }
  },
  computed: {
    i18n () {
      return I18N
    },
    isSubmittingTask () {
      return this.$store.state.subjects.isSubmittingTask
    },
    isSchoolFeatureLocalePool () {
      return this.$store.getters.isSchoolFeatureLocalePool
    },
    isKindergarten () {
      return this.$store.getters.isKindergarten
    },
    schoolCountry () {
      const school = this.$store.state.activeSchool || window.SCHOOL
      return school && school.country ? school.country : null
    },
    localPoolTitle () {
      const title = this.$store.getters.localPoolTitle
      return title || this.i18n[this.locale]['pool']
    },
    subjects () {
      return this.$store.state.subjects.subjects || []
    },
    backendTasks () {
      return this.$store.state.subjects.tasks || []
    },
    // Re-implements the backend VIEW `public.tasks` client-side by grouping
    // rows from `readSubjects`. Matches the CTE + GROUP BY in
    // ../docendo/db/structure.sql (CREATE VIEW public.tasks).
    tasks () {
      const cals = this.allCalendarsInHash
      const backendTaskBySubjectId = {}
      this.backendTasks.forEach(bt => {
        (bt.subject_ids || []).forEach(sid => { backendTaskBySubjectId[sid] = bt })
      })
      const map = {}
      this.subjects.forEach(s => {
        const owner = cals[s.calendar_id]
        const isAlways = TASK_ALWAYS_TYPES.indexOf(s.subject_type) > -1
        const isTeaching = TASK_TEACHING_TYPES.indexOf(s.subject_type) > -1
        const ownerIsUser = owner && owner.type === 'user'
        const ownerIsNull = !s.calendar_id || !owner
        const included = isAlways || (isTeaching && (ownerIsUser || ownerIsNull))
        if (!included) return

        // Only user-owned tiles are "own calendar". Club/other shared
        // calendars keep users on user_calendars so they show in Brugere.
        const inUserCalendar = !owner || owner.type === 'user'
        const userCalendarsOfSubject = []
        const seenUserCals = {}
        const addUserCal = (id) => {
          if (id == null || seenUserCals[id]) return
          const cal = cals[id]
          if (cal && cal.type === 'user') {
            seenUserCals[id] = true
            userCalendarsOfSubject.push(id)
          }
        }
        ;(s.calendar_ids || []).forEach(addUserCal)
        if (Array.isArray(s.cals)) {
          s.cals.forEach(cal => {
            if (cal && cal.calendar_type === 'user') addUserCal(cal.id)
          })
        }
        const hasEvents = s.can_delete === false

        const hoursKey = s.hours == null ? '' : s.hours
        const prepKey = s.preparation == null ? '' : s.preparation
        const subjectKey = this.unmergeTasks ? s.id : ''
        const ownerKey = inUserCalendar ? '' : (s.calendar_id || '')
        const key = [
          s.school_id,
          s.school_year_id,
          s.subject_type,
          hoursKey,
          inUserCalendar,
          s.can_schedule,
          s.name,
          prepKey,
          subjectKey,
          ownerKey
        ].join(';')

        if (!map[key]) {
          map[key] = {
            id: [
              s.school_id,
              s.school_year_id,
              s.subject_type,
              hoursKey,
              inUserCalendar,
              s.can_schedule,
              s.name,
              prepKey,
              subjectKey,
              ownerKey
            ].join(';'),
            school_id: s.school_id,
            school_year_id: s.school_year_id,
            name: s.name,
            subject_type: s.subject_type,
            hours: parseFloat(s.hours) || 0,
            preparation: s.preparation,
            can_schedule: s.can_schedule,
            in_user_calendar: inUserCalendar,
            color: s.color,
            default_duration: s.default_duration,
            from_date: s.from_date || null,
            to_date: s.to_date || null,
            belongs_to_calendars: [],
            user_calendars: [],
            subject_ids: [],
            subjects: []
          }
        }
        const group = map[key]

        if (s.color != null && (group.color == null || String(s.color) < String(group.color))) {
          group.color = s.color
        }
        if (s.default_duration != null && (group.default_duration == null || s.default_duration < group.default_duration)) {
          group.default_duration = s.default_duration
        }
        if (s.from_date != null && (group.from_date == null || s.from_date > group.from_date)) {
          group.from_date = s.from_date
        }
        if (s.to_date != null && (group.to_date == null || s.to_date > group.to_date)) {
          group.to_date = s.to_date
        }

        group.belongs_to_calendars.push(s.calendar_id)
        userCalendarsOfSubject.forEach(id => group.user_calendars.push(id))
        group.subject_ids.push(s.id)
        group.subjects.push({
          id: s.id,
          calendar_id: s.calendar_id,
          has_events: hasEvents,
          user_calendars: userCalendarsOfSubject.slice()
        })
      })

      const tasks = Object.values(map)
      tasks.forEach(t => {
        t.user_calendar_ids = t.in_user_calendar ? t.belongs_to_calendars.slice() : t.user_calendars.slice()
        // Prefer the backend-provided task id (from state.subjects.tasks) so
        // mutation endpoints receive a matching id. Falls back to the locally
        // generated id if backend tasks aren't loaded or don't overlap.
        if (t.subject_ids && t.subject_ids.length) {
          const bt = backendTaskBySubjectId[t.subject_ids[0]]
          if (bt && bt.id) {
            t._generatedId = t.id
            t.id = bt.id
          }
        }
      })
      return tasks
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    visibleTasks () {
      const calendarsHash = this.allCalendarsInHash
      return this.tasks.filter(task => {
        const subjects = task.subjects || []
        if (subjects.length === 0) return true
        return !subjects.every(s => {
          const ownerCal = calendarsHash[s.calendar_id]
          return ownerCal && ownerCal.type === 'klass'
        })
      })
    },
    tasksTeaching () {
      return this.visibleTasks.filter(t => t.subject_type === 'person_resource_visible' || t.subject_type === 'person_resource' || t.subject_type === 'subject')
    },
    tasksTasks () {
      return this.visibleTasks.filter(t => t.subject_type === 'additional_tasks')
    },
    tasksPrep () {
      return this.visibleTasks.filter(t => t.subject_type === 'preparation')
    },
    tasksPool () {
      return this.visibleTasks.filter(t => t.subject_type === 'pool')
    },
    tasksClub () {
      return this.visibleTasks.filter(t => t.subject_type === 'club')
    },
    tasksAvailable () {
      return this.visibleTasks.filter(t => t.subject_type === 'available')
    },
    filters () {
      const filters = [
        {label: this.i18n[this.locale]['teaching'], value: 'teaching', length: this.tasksTeaching.length},
        {label: this.i18n[this.locale]['additional_tasks'], value: 'additional_tasks', length: this.tasksTasks.length},
        {label: this.i18n[this.locale]['preparation'], value: 'preparation', length: this.tasksPrep.length},
        {label: this.localPoolTitle, value: 'pool', length: this.tasksPool.length},
        {label: this.i18n[this.locale]['club'], value: 'club', length: this.tasksClub.length},
        {label: this.i18n[this.locale]['available'], value: 'available', length: this.tasksAvailable.length}
      ]
      return this.isSchoolFeatureLocalePool ? filters : filters.filter(f => f.value !== 'pool')
    },
    filterLabels () {
      return this.filters.map(f => f.label)
    },
    selectedFilterSubjectTypesValue () {
      return this.filters
        .filter(f => this.selectedFilterSubjectTypes.includes(f.label))
        .map(f => f.value)
    },
    taskNameLabels () {
      const names = this.visibleTasks
        .map(t => t && t.name ? String(t.name).trim() : '')
        .filter(n => n.length > 0)
      return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b))
    },
    onlyTasksWithUsersI18n () {
      return this.i18n[this.locale].only_tasks_with_users
    },
    onlyTasksWithoutUsersI18n () {
      return this.i18n[this.locale].only_tasks_without_users
    },
    taskStatusFilters () {
      return [this.onlyTasksWithUsersI18n, this.onlyTasksWithoutUsersI18n]
    },
    filteredTasks () {
      const calendarsHash = this.allCalendarsInHash
      const sanitize = task => {
        const all = task.user_calendar_ids || []
        const users = []
        const nonUsers = []
        all.forEach(calId => {
          const cal = calendarsHash[calId]
          if (cal && cal.type === 'user') {
            users.push(calId)
          } else if (calId != null) {
            nonUsers.push(calId)
          }
        })
        users.sort((a, b) => (calendarsHash[a].name || '').localeCompare(calendarsHash[b].name || '', 'da'))
        return Object.assign({}, task, {
          user_calendar_ids: users,
          _nonUserCalendarIds: nonUsers
        })
      }

      const selectedValues = this.selectedFilterSubjectTypesValue
      let base = []
      if (!selectedValues.length || selectedValues.includes('')) {
        base = this.visibleTasks.map(sanitize)
      } else {
        if (selectedValues.includes('teaching')) base = base.concat(this.tasksTeaching.map(sanitize))
        if (selectedValues.includes('additional_tasks')) base = base.concat(this.tasksTasks.map(sanitize))
        if (selectedValues.includes('preparation')) base = base.concat(this.tasksPrep.map(sanitize))
        if (selectedValues.includes('pool')) base = base.concat(this.tasksPool.map(sanitize))
        if (selectedValues.includes('club')) base = base.concat(this.tasksClub.map(sanitize))
        if (selectedValues.includes('available')) base = base.concat(this.tasksAvailable.map(sanitize))
      }
      if (this.selectedFilterTaskNames && this.selectedFilterTaskNames.length > 0) {
        const selectedNames = this.selectedFilterTaskNames
        base = base.filter(t => {
          const n = t && t.name ? String(t.name).trim() : ''
          return n.length > 0 && selectedNames.indexOf(n) > -1
        })
      }
      if (this.selectedFilterTaskStatus && this.selectedFilterTaskStatus.length > 0) {
        const hasFilterWithUsers = this.selectedFilterTaskStatus.indexOf(this.onlyTasksWithUsersI18n) > -1
        const hasFilterWithoutUsers = this.selectedFilterTaskStatus.indexOf(this.onlyTasksWithoutUsersI18n) > -1
        base = base.filter(task => {
          const hasUsers = (task.user_calendar_ids || []).length > 0
          return (hasFilterWithUsers && hasUsers) || (hasFilterWithoutUsers && !hasUsers)
        })
      }
      return base
    },
    groupedTasks () {
      const map = {}
      this.filteredTasks.forEach(task => {
        const name = (task.name || '').trim()
        if (!name) return
        if (!map[name]) {
          map[name] = {
            name: name,
            color: task.color || '#ccc',
            tasks: [],
            totalHours: 0,
            weightedHours: 0
          }
        }
        map[name].tasks.push(task)
        const h = parseFloat(task.hours) || 0
        map[name].totalHours += h
        map[name].weightedHours += this.getTaskWeightedTotal(task)
      })
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredGroupedTasks () {
      return this.groupedTasks
    },
    totalGroupCount () {
      const names = new Set()
      this.visibleTasks.forEach(t => {
        const n = (t.name || '').trim()
        if (n) names.add(n)
      })
      return names.size
    },
    allFilteredWeightedHours () {
      let sum = 0
      this.filteredGroupedTasks.forEach(g => { sum += g.weightedHours })
      return sum
    },
    allGroupData () {
      const allTasks = []
      let totalHours = 0
      let weightedHours = 0
      this.filteredGroupedTasks.forEach(g => {
        g.tasks.forEach(t => allTasks.push(t))
        totalHours += g.totalHours
        weightedHours += g.weightedHours
      })
      const sorted = allTasks.slice().sort((a, b) => {
        const nameCmp = (a.name || '').localeCompare(b.name || '')
        if (nameCmp !== 0) return nameCmp
        return (parseFloat(b.hours) || 0) - (parseFloat(a.hours) || 0)
      })
      return { name: this.i18n[this.locale]['all'], color: '#8898aa', tasks: sorted, totalHours: totalHours, weightedHours: weightedHours }
    },
    activeGroupName () {
      if (this.activeGroup === '__all__') return '__all__'
      if (this.activeGroup) {
        const exists = this.filteredGroupedTasks.find(g => g.name === this.activeGroup)
        if (exists) return this.activeGroup
      }
      return '__all__'
    },
    activeGroupData () {
      if (this.activeGroupName === '__all__') return this.allGroupData
      const group = this.filteredGroupedTasks.find(g => g.name === this.activeGroupName)
      if (!group) return null
      const sorted = group.tasks.slice().sort((a, b) => {
        const nameCmp = (a.name || '').localeCompare(b.name || '')
        if (nameCmp !== 0) return nameCmp
        return (parseFloat(b.hours) || 0) - (parseFloat(a.hours) || 0)
      })
      return Object.assign({}, group, { tasks: sorted })
    },
    visibleColumnCount () {
      let count = 4
      if (this.showCalendarInTable) count++
      if (this.showDatesInTable) count++
      if (this.showUsersInTable) count++
      if (this.showTotalInTable) count++
      if (this.bulkActionMode) count++
      return count
    },
    taskByRowId () {
      const map = {}
      this.tasks.forEach(task => {
        const rowId = task._generatedId || task.id
        map[rowId] = task
      })
      return map
    },
    visibleRowIds () {
      if (!this.activeGroupData) return []
      return this.activeGroupData.tasks.map(task => task._generatedId || task.id)
    },
    allVisibleSelected () {
      if (this.visibleRowIds.length === 0) return false
      return this.visibleRowIds.every(rowId => this.selectedRowIds.indexOf(rowId) !== -1)
    },
    bulkSubjectTypeOptions () {
      const stripHtml = html => {
        if (!html) return ''
        const tmp = document.createElement('div')
        tmp.innerHTML = html
        return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim()
      }
      const dbSubjectTypes = this.calSubjectTypes.filter(type => !(type === 'other_stuff' || type === 'other_tasks'))
      const typesMap = this.isKindergarten ? subjectKindergartenTypes : subjectAllTypes
      let arr = []
      dbSubjectTypes.forEach(activeType => {
        if (!typesMap[activeType]) return
        const localeEntry = typesMap[activeType].filter(t => t.lang === this.locale)[0]
        if (localeEntry) arr.push(localeEntry)
      })
      if (this.localPoolTitle) {
        arr = arr.map(t => t.value === 'pool' ? Object.assign({}, t, {name: this.localPoolTitle}) : t)
      }
      if (!this.isSchoolFeatureLocalePool) {
        arr = arr.filter(t => t.value !== 'pool')
      }
      arr.sort((a, b) => (a.order > b.order) ? 1 : -1)
      return arr.map(t => ({value: t.value, label: stripHtml(t.name)}))
    },
    bulkDateMin () {
      if (this.activeSchoolYear && this.activeSchoolYear.employee_time_start) {
        return moment(this.activeSchoolYear.employee_time_start)
      }
      return null
    },
    bulkDateMax () {
      if (this.activeSchoolYear && this.activeSchoolYear.employee_time_end) {
        return moment(this.activeSchoolYear.employee_time_end)
      }
      return null
    },
    selectedSubjectIds () {
      const ids = []
      const seen = {}
      this.selectedRowIds.forEach(rowId => {
        const task = this.taskByRowId[rowId]
        if (!task) return
        const subjectIds = task.subject_ids || []
        subjectIds.forEach(sid => {
          if (!seen[sid]) {
            seen[sid] = true
            ids.push(sid)
          }
        })
      })
      return ids
    },
    deletableSubjectIds () {
      const ids = []
      const seen = {}
      this.selectedRowIds.forEach(rowId => {
        const task = this.taskByRowId[rowId]
        if (!task) return
        const subjects = task.subjects || []
        subjects.forEach(subj => {
          if (subj.has_events) return
          if (!seen[subj.id]) {
            seen[subj.id] = true
            ids.push(subj.id)
          }
        })
      })
      return ids
    },
    skippedSubjectCount () {
      return this.selectedSubjectIds.length - this.deletableSubjectIds.length
    }
  },
  watch: {
    showUsersInTable (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionTasksFromSubjectsShowUsers', val)
    },
    showCalendarInTable (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionTasksFromSubjectsShowCalendar', val)
    },
    showTotalInTable (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionTasksFromSubjectsShowTotal', val)
    },
    showDatesInTable (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionTasksFromSubjectsShowDates', val)
    },
    sidebarCollapsed (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionTasksFromSubjectsSidebarCollapsed', val)
    },
    unmergeTasks (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionTasksFromSubjectsUnmergeTasks', val)
    }
  },
  created () {
    const storedShowUsers = this.$helpers.getLocalStorage('doSubjectsDistributionTasksFromSubjectsShowUsers')
    if (storedShowUsers === 'false') {
      this.showUsersInTable = false
    }
    const storedShowCalendar = this.$helpers.getLocalStorage('doSubjectsDistributionTasksFromSubjectsShowCalendar')
    if (storedShowCalendar === 'false') {
      this.showCalendarInTable = false
    }
    const storedShowTotal = this.$helpers.getLocalStorage('doSubjectsDistributionTasksFromSubjectsShowTotal')
    if (storedShowTotal === 'false') {
      this.showTotalInTable = false
    }
    const storedShowDates = this.$helpers.getLocalStorage('doSubjectsDistributionTasksFromSubjectsShowDates')
    if (storedShowDates === 'true') {
      this.showDatesInTable = true
    }
    const storedSidebarCollapsed = this.$helpers.getLocalStorage('doSubjectsDistributionTasksFromSubjectsSidebarCollapsed')
    if (storedSidebarCollapsed === 'true') {
      this.sidebarCollapsed = true
    }
    const storedUnmergeTasks = this.$helpers.getLocalStorage('doSubjectsDistributionTasksFromSubjectsUnmergeTasks')
    if (storedUnmergeTasks === 'true') {
      this.unmergeTasks = true
    }
    docendo.subjectsForCalendarType('user', this.schoolCountry, data => {
      this.calSubjectTypes = data || []
    })
  },
  methods: {
    getTaskWeightedTotal (task) {
      const baseHours = parseFloat(task.hours) || 0
      const userCalIds = task.user_calendar_ids || []
      if (!userCalIds.length) return 0

      const subjectId = task.subjects && task.subjects.length
        ? task.subjects[0].id
        : (task.subject_ids && task.subject_ids.length ? task.subject_ids[0] : null)
      const subj = subjectId ? this.subjectsInHash[subjectId] : null
      const scList = subj && subj.subject_calendars ? subj.subject_calendars : []

      let total = 0
      for (let i = 0; i < userCalIds.length; i++) {
        const sc = scList.find(s => s.calendar_id === userCalIds[i] && s.hours != null)
        total += sc ? parseFloat(sc.hours) : baseHours
      }
      return parseFloat(total.toFixed(2))
    },
    toggleFilter (filter, filterArray) {
      if (filterArray.includes(filter)) {
        filterArray.splice(filterArray.indexOf(filter), 1)
      } else {
        filterArray.push(filter)
      }
    },
    toggleFilterTaskNames (tag) {
      this.toggleFilter(tag, this.selectedFilterTaskNames)
    },
    toggleFilterSubjectTypes (tag) {
      this.toggleFilter(tag, this.selectedFilterSubjectTypes)
    },
    toggleFilterTaskStatus (tag) {
      this.toggleFilter(tag, this.selectedFilterTaskStatus)
    },
    getTaskTypeLabel (task) {
      const t = task.subject_type
      if (t === 'person_resource_visible' || t === 'person_resource' || t === 'subject') return this.i18n[this.locale]['teaching']
      if (t === 'additional_tasks') return this.i18n[this.locale]['additional_tasks']
      if (t === 'preparation') return this.i18n[this.locale]['preparation']
      if (t === 'pool') return this.localPoolTitle
      if (t === 'club') return this.i18n[this.locale]['club']
      if (t === 'available') return this.i18n[this.locale]['available']
      return ''
    },
    setActiveGroup (name) {
      this.activeGroup = name
    },
    getUserNames (calIds) {
      return calIds
        .map(calId => this.allCalendarsInHash[calId] ? this.allCalendarsInHash[calId].name : '')
        .filter(n => n.length > 0)
    },
    getUsersWithEvents (task) {
      const usersWithEvents = []
      const seen = {}
      const subjects = task.subjects || []
      subjects.forEach(subject => {
        if (!subject.has_events) return
        if (subject.user_calendars && subject.user_calendars.length > 0) {
          subject.user_calendars.forEach(calId => {
            if (!seen[calId]) {
              seen[calId] = true
              usersWithEvents.push(calId)
            }
          })
        } else if (subject.calendar_id != null) {
          if (!seen[subject.calendar_id]) {
            seen[subject.calendar_id] = true
            usersWithEvents.push(subject.calendar_id)
          }
        }
      })
      return usersWithEvents
    },
    startBulkAction (actionType) {
      this.bulkActionMode = true
      this.bulkActionType = actionType
      this.bulkSubjectType = ''
      this.bulkHours = null
      this.isApplyingBulk = false
      if (actionType === 'dates') {
        this.bulkFromDate = this.bulkDateMin ? this.bulkDateMin.clone() : moment()
        this.bulkToDate = this.bulkDateMax ? this.bulkDateMax.clone() : moment()
      } else {
        this.bulkFromDate = null
        this.bulkToDate = null
      }
    },
    cancelBulkAction () {
      this.bulkActionMode = false
      this.bulkActionType = ''
      this.selectedRowIds = []
      this.bulkSubjectType = ''
      this.bulkHours = null
      this.bulkFromDate = null
      this.bulkToDate = null
      this.isApplyingBulk = false
    },
    isRowSelected (task) {
      const rowId = task._generatedId || task.id
      return this.selectedRowIds.indexOf(rowId) !== -1
    },
    toggleRowSelection (task) {
      const rowId = task._generatedId || task.id
      const idx = this.selectedRowIds.indexOf(rowId)
      if (idx === -1) {
        this.selectedRowIds.push(rowId)
      } else {
        this.selectedRowIds.splice(idx, 1)
      }
    },
    toggleSelectAllVisible () {
      if (this.allVisibleSelected) {
        this.selectedRowIds = this.selectedRowIds.filter(rowId => this.visibleRowIds.indexOf(rowId) === -1)
      } else {
        const next = this.selectedRowIds.slice()
        this.visibleRowIds.forEach(rowId => {
          if (next.indexOf(rowId) === -1) next.push(rowId)
        })
        this.selectedRowIds = next
      }
    },
    setBulkFromDate (date) {
      this.bulkFromDate = date
    },
    setBulkToDate (date) {
      this.bulkToDate = date
    },
    reloadSubjects () {
      if (!this.activeSchoolYear || !this.activeSchoolYear.id) {
        this.$store.state.subjects.isSubmittingTask = false
        return
      }
      const schoolId = this.$store.getters.schoolId
      this.$store.state.subjects.isSubmittingTask = true
      docendo.readSubjects(schoolId, this.activeSchoolYear.id, null, data => {
        this.$store.commit('setSubjects', data)
        this.$store.state.subjects.isSubmittingTask = false
      }, () => {
        this.$store.state.subjects.isSubmittingTask = false
      })
    },
    runBulkUpdate (changes, successKey, failureKey) {
      const subjectIds = this.selectedSubjectIds
      if (subjectIds.length === 0) return
      const schoolId = this.$store.getters.schoolId
      this.isApplyingBulk = true
      docendo.bulkUpdateSubjects(schoolId, subjectIds, changes, data => {
        const subjects = typeof data === 'string' ? JSON.parse(data) : data
        if (Array.isArray(subjects)) {
          subjects.forEach(subj => {
            subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : (subj.calendar_ids || [])
            this.$store.commit('updateSubject', subj)
          })
        }
        this.reloadSubjects()
        eventbus.toast = {text: this.i18n[this.locale][successKey], success: true}
        this.cancelBulkAction()
      }, err => {
        this.isApplyingBulk = false
        const fallback = this.i18n[this.locale][failureKey]
        const msg = err && err.data && err.data.error ? err.data.error : (err && err.error ? err.error : fallback)
        eventbus.toast = {text: msg, success: false}
      })
    },
    applyBulkSubjectType () {
      if (!this.bulkSubjectType || this.isApplyingBulk) return
      this.runBulkUpdate({subject_type: this.bulkSubjectType}, 'bulk_subject_type_updated', 'bulk_subject_type_failed')
    },
    applyBulkHours () {
      if (this.bulkHours == null || this.bulkHours === '' || this.isApplyingBulk) return
      const hours = parseFloat(this.bulkHours)
      if (isNaN(hours) || hours < 0) return
      this.runBulkUpdate({hours: hours}, 'bulk_hours_updated', 'bulk_hours_failed')
    },
    applyBulkDates () {
      if (!this.bulkFromDate || !this.bulkToDate || this.isApplyingBulk) return
      const fromMoment = moment(this.bulkFromDate)
      const toMoment = moment(this.bulkToDate)
      if (this.bulkDateMin && fromMoment.isBefore(this.bulkDateMin, 'day')) {
        eventbus.toast = {text: this.i18n[this.locale]['bulk_dates_out_of_range'], success: false}
        return
      }
      if (this.bulkDateMax && toMoment.isAfter(this.bulkDateMax, 'day')) {
        eventbus.toast = {text: this.i18n[this.locale]['bulk_dates_out_of_range'], success: false}
        return
      }
      this.runBulkUpdate({
        from_date: fromMoment.format('YYYY-MM-DD'),
        to_date: toMoment.format('YYYY-MM-DD')
      }, 'bulk_dates_updated', 'bulk_dates_failed')
    },
    applyBulkDelete () {
      if (this.isApplyingBulk) return
      const deletable = this.deletableSubjectIds
      if (deletable.length === 0) {
        eventbus.toast = {text: this.i18n[this.locale]['bulk_delete_all_skipped'], success: false}
        return
      }
      if (!window.confirm(this.i18n[this.locale]['bulk_delete_confirm'])) return
      const schoolId = this.$store.getters.schoolId
      this.isApplyingBulk = true
      docendo.bulkDeleteSubjects(schoolId, deletable, data => {
        const result = typeof data === 'string' ? JSON.parse(data) : data
        if (result && Array.isArray(result.deleted)) {
          result.deleted.forEach(subj => {
            this.$store.commit('deleteSubject', subj)
            window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: subj}))
          })
        }
        this.reloadSubjects()
        const errors = result && Array.isArray(result.errors) ? result.errors : []
        if (errors.length > 0) {
          eventbus.toast = {text: errors.length + ' ' + this.i18n[this.locale]['bulk_delete_skipped'], success: false}
        } else {
          eventbus.toast = {text: this.i18n[this.locale]['bulk_delete_success'], success: true}
        }
        this.cancelBulkAction()
      }, err => {
        this.isApplyingBulk = false
        const fallback = this.i18n[this.locale]['bulk_delete_failed']
        const msg = err && err.data && err.data.error ? err.data.error : (err && err.error ? err.error : fallback)
        eventbus.toast = {text: msg, success: false}
      })
    },
    getCalendarName (calId) {
      const cal = this.allCalendarsInHash[calId]
      return cal ? cal.name : ''
    },
    getTaskCalendarLabel (task) {
      if (task.in_user_calendar) return this.i18n[this.locale]['own_calendar']
      const calIds = task.belongs_to_calendars || []
      for (let i = 0; i < calIds.length; i++) {
        const cal = this.allCalendarsInHash[calIds[i]]
        if (cal && cal.type !== 'user') return cal.name
      }
      return ''
    },
    getTaskDatesLabel (task) {
      const fmt = d => d ? moment(d).format('D. MMM YYYY') : ''
      const from = fmt(task.from_date)
      const to = fmt(task.to_date)
      if (from && to) return from + ' – ' + to
      return from || to
    },
    setEditTask (task) {
      this.isTaskModal = false
      this.isQuickCreate = false
      this.editTask = task
      this.isLoadingFreshTask = true
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
      const schoolId = this.$store.getters.schoolId
      const onLoaded = () => {
        const rowId = task._generatedId || task.id
        let fresh = this.tasks.find(t => (t._generatedId || t.id) === rowId)
        if (!fresh && task.subject_ids && task.subject_ids.length) {
          const cachedSubjectIds = new Set(task.subject_ids)
          fresh = this.tasks.find(t => (t.subject_ids || []).some(sid => cachedSubjectIds.has(sid)))
        }
        if (fresh) {
          this.editTask = fresh
        }
        this.isLoadingFreshTask = false
      }
      docendo.readSubjects(schoolId, this.activeSchoolYear.id, null, data => {
        this.$store.commit('setSubjects', data)
        onLoaded()
      }, () => {
        onLoaded()
      })
    },
    openCreateTask () {
      this.editTask = null
      this.prefillTask = null
      this.isQuickCreate = false
      this.isTaskModal = true
    },
    closeCreateTask () {
      this.isTaskModal = false
      this.editTask = null
      this.prefillTask = null
      this.isQuickCreate = false
    },
    duplicateFromGroup () {
      if (!this.activeGroupData) return
      const ref = this.activeGroupData.tasks[0]
      this.editTask = null
      this.isQuickCreate = true
      this.prefillTask = {
        name: this.activeGroupData.name,
        color: this.activeGroupData.color,
        subject_type: ref ? ref.subject_type : 'additional_tasks',
        can_schedule: ref ? ref.can_schedule : true,
        default_duration: ref ? ref.default_duration : 60,
        preparation: ref ? ref.preparation : null,
        in_user_calendar: ref ? ref.in_user_calendar : true,
        belongs_to_calendars: ref ? ref.belongs_to_calendars : []
      }
      this.isTaskModal = false
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
    },
    duplicateTask (task) {
      this.editTask = null
      this.isQuickCreate = true
      this.prefillTask = {
        name: task.name,
        color: task.color,
        subject_type: task.subject_type,
        can_schedule: task.can_schedule,
        default_duration: task.default_duration,
        preparation: task.preparation,
        in_user_calendar: task.in_user_calendar,
        belongs_to_calendars: task.belongs_to_calendars || []
      }
      this.isTaskModal = false
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
    },
    createTask ({keepOpen = false} = {}) {
      if (!keepOpen) {
        this.closeCreateTask()
        return
      }
      this.isTaskModal = false
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
    },
    setActiveDSelect (task) {
      if ((task.user_calendar_ids || []).length < 1) {
        this.activeDSelectShow = true
      } else {
        this.activeDSelectShow = false
      }
      this.activeDSelect = task._generatedId || task.id
    },
    closeDSelect () {
      if (!this.activeDSelect) return
      this.activeDSelect = null
    },
    setTableSelectedUsers (users, task) {
      const newCalIds = users.map(u => u.id)
      const oldCalIds = (task.user_calendar_ids || []).filter(id => id != null)
      const addedCalIds = newCalIds.filter(id => oldCalIds.indexOf(id) === -1)
      const removedCalIds = oldCalIds.filter(id => newCalIds.indexOf(id) === -1)
      if (addedCalIds.length === 0 && removedCalIds.length === 0) return

      const schoolId = task.school_id
      const schoolYearId = task.school_year_id
      const subjectIds = (task.subject_ids || []).slice()

      const baseSubjectPayload = () => ({
        name: task.name,
        color: task.color,
        hours: task.hours,
        subject_type: task.subject_type,
        default_duration: task.default_duration,
        preparation: task.preparation,
        can_schedule: task.can_schedule,
        from_date: task.from_date || null,
        to_date: task.to_date || null
      })

      const operations = []

      if (task.in_user_calendar) {
        const existingPlaceholder = (task.subjects || []).find(s => !s.calendar_id)
        const subjectsToDelete = (task.subjects || [])
          .filter(s => s.calendar_id && removedCalIds.indexOf(s.calendar_id) !== -1)
          .map(s => s.id)
        if (newCalIds.length > 0 && existingPlaceholder) {
          subjectsToDelete.push(existingPlaceholder.id)
        }
        if (subjectsToDelete.length > 0) {
          operations.push(new Promise((resolve, reject) => {
            docendo.bulkDeleteSubjects(schoolId, subjectsToDelete, data => {
              const result = typeof data === 'string' ? JSON.parse(data) : data
              if (result && Array.isArray(result.deleted)) {
                result.deleted.forEach(subj => {
                  this.$store.commit('deleteSubject', subj)
                  window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: subj}))
                })
              }
              if (result && Array.isArray(result.errors) && result.errors.length > 0) {
                eventbus.toast = {text: 'Nogle brugere kunne ikke fjernes, da de har skemalagte brikker', success: false}
              }
              resolve()
            }, err => reject(err))
          }))
        }

        const subjectsToCreate = addedCalIds.map(calId => Object.assign({}, baseSubjectPayload(), {
          calendar_id: calId,
          calendar_ids: []
        }))
        if (newCalIds.length === 0 && !existingPlaceholder) {
          subjectsToCreate.push(Object.assign({}, baseSubjectPayload(), {
            calendar_ids: []
          }))
        }
        if (subjectsToCreate.length > 0) {
          operations.push(new Promise((resolve, reject) => {
            docendo.bulkCreateSubjects(schoolId, schoolYearId, subjectsToCreate, data => {
              const created = typeof data === 'string' ? JSON.parse(data) : data
              if (Array.isArray(created)) {
                created.forEach(subj => {
                  subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : (subj.calendar_ids || [])
                  this.$store.commit('createSubject', subj)
                  window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: subj}))
                })
              }
              resolve()
            }, err => reject(err))
          }))
        }
      } else {
        if (subjectIds.length === 0) return
        const targetSubjectId = subjectIds[0]
        addedCalIds.forEach(calId => {
          operations.push(new Promise((resolve, reject) => {
            docendo.bulkUpdateSubjects(schoolId, [targetSubjectId], {add_calendar_id: calId}, data => {
              const subjects = typeof data === 'string' ? JSON.parse(data) : data
              if (Array.isArray(subjects)) {
                subjects.forEach(subj => {
                  subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : (subj.calendar_ids || [])
                  this.$store.commit('updateSubject', subj)
                  window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: subj}))
                })
              }
              resolve()
            }, err => reject(err))
          }))
        })
        removedCalIds.forEach(calId => {
          operations.push(new Promise((resolve, reject) => {
            docendo.bulkUpdateSubjects(schoolId, [targetSubjectId], {remove_calendar_id: calId}, data => {
              const subjects = typeof data === 'string' ? JSON.parse(data) : data
              if (Array.isArray(subjects)) {
                subjects.forEach(subj => {
                  subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : (subj.calendar_ids || [])
                  this.$store.commit('updateSubject', subj)
                  window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: subj}))
                })
              }
              resolve()
            }, err => reject(err))
          }))
        })
      }

      if (operations.length === 0) {
        this.reloadSubjects()
        return
      }

      this.$store.state.subjects.isSubmittingTask = true
      Promise.all(operations).then(() => {
        this.reloadSubjects()
      }).catch(err => {
        console.warn('setTableSelectedUsers failed', err)
        const fallback = 'Kunne ikke opdatere brugere på opgaven'
        const msg = err && err.data && err.data.error ? err.data.error : (err && err.error ? err.error : fallback)
        eventbus.toast = {text: msg, success: false}
        this.reloadSubjects()
      })
    },
    dataToExcel () {
      if (!this.activeGroupData) return []
      return this.activeGroupData.tasks.map(task => {
        const row = {
          name: task.name || '',
          hours: parseFloat(task.hours) || 0,
          type: this.getTaskTypeLabel(task)
        }
        if (this.showCalendarInTable) row.calendar = this.getTaskCalendarLabel(task)
        if (this.showDatesInTable) row.dates = this.getTaskDatesLabel(task)
        if (this.showUsersInTable) row.users = this.getUserNames(task.user_calendar_ids || []).join(', ')
        if (this.showTotalInTable) row.total = this.getTaskWeightedTotal(task)
        return row
      })
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.downloadExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(() => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.downloadExcel()
          }
        }, 500)
      }
    },
    downloadExcel () {
      const t = this.i18n[this.locale]
      const headers = {
        name: t['task'],
        hours: t['hours'],
        type: t['type']
      }
      if (this.showCalendarInTable) headers.calendar = t['calendar']
      if (this.showDatesInTable) headers.dates = t['dates']
      if (this.showUsersInTable) headers.users = t['users']
      if (this.showTotalInTable) headers.total = t['total']
      const rows = this.dataToExcel()
      rows.unshift(headers)

      const sheet = XLSX.utils.json_to_sheet(rows, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      const groupName = this.activeGroupName === '__all__' ? t['all'] : this.activeGroupName
      XLSX.utils.book_append_sheet(wb, sheet, t['tasks_sheet'])
      XLSX.writeFile(wb, t['tasks_sheet'].toLowerCase() + '_' + groupName.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '_') + '.xlsx')

      this.isDownloading = false
    }
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.tasks-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
}

.tasks-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 6;
  border-bottom: 1px solid var(--border-color);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.tasks-header__right,
.tasks-header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.tasks-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.tasks-sidebar {
  width: 260px;
  min-width: 260px;
  border-right: 1px solid #e2e8f0;
  background: #f8fafc;
  overflow-y: auto;
  transition: width 0.2s, min-width 0.2s;
}

.tasks-sidebar--collapsed {
  width: 42px;
  min-width: 42px;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8898aa;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
}

.sidebar-create-btn {
  padding: 2px 10px;
  font-size: 14px;
  line-height: 1;
  min-height: 0;
}

.sidebar-collapse-btn {
  background: none;
  border: none;
  color: #8898aa;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s;
}

.sidebar-collapse-btn:hover {
  color: #525f7f;
}

.tasks-sidebar--collapsed .sidebar-header {
  justify-content: center;
  padding: 12px 8px;
}

.sidebar-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sidebar-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  font-size: 13px;
  transition: background 0.1s;
}

.sidebar-item:hover {
  background: #edf2f7;
}

.sidebar-item--active {
  background: #ffffff;
  box-shadow: inset 3px 0 0 var(--primary);
  font-weight: 600;
}

.sidebar-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-count {
  font-size: 11px;
  color: #8898aa;
  margin-left: 8px;
  flex-shrink: 0;
}

.tasks-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
}

.thead-summary th {
  background: #f8fafc;
  padding: 10px 12px;
  border-bottom: none;
  box-shadow: inset 0 -1px 0 #e2e8f0;
}

.thead-summary__dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 8px;
  vertical-align: middle;
}

.thead-summary__name {
  font-size: 14px;
  font-weight: 600;
  margin-right: 10px;
  text-transform: none;
  letter-spacing: 0;
}

.thead-summary__meta {
  font-size: 12px;
  color: #8898aa;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.task-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.task-table-wrap .d-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.task-table-wrap .d-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8898aa;
  height: 52.5px;
  border-bottom: none;
  box-shadow: inset 0 -1px 0 #e2e8f0;
}

.task-table-wrap .d-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f3f7;
}

.task-table-row {
  cursor: pointer;
  transition: background 0.1s;
}

.task-table-row:hover {
  background: #f8fafc;
}

.task-table-row--add {
  color: #8898aa;
  font-style: italic;
}

.task-table-row--add:hover {
  color: var(--primary);
}

.task-table-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
}

.task-table-type {
  font-size: 12px;
  color: #8898aa;
}

.task-table-calendar {
  font-size: 12px;
  color: #525f7f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.task-table-calendar--own {
  color: #8898aa;
  font-style: italic;
}

.task-table-dates {
  font-size: 12px;
  color: #525f7f;
  white-space: nowrap;
}

.task-table-actions {
  text-align: right;
  width: 40px;
}

.task-table-copy {
  border: none;
  background: rgba(0, 0, 0, 0.06);
  color: #525f7f;
  width: 22px;
  height: 22px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s;
  line-height: 1;
  padding: 0;
}

.task-table-row:hover .task-table-copy {
  opacity: 1;
}

.task-table-copy:hover {
  background: rgba(0, 0, 0, 0.12);
}

.task__name {
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
}

.task__name .td-subject--hidden {
  background: transparent !important;
}

.td-subject {
  position: relative;
  padding: 4px 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgb(0 0 0 / 30%);
  text-align: left;
  min-width: auto;
  font-weight: bold;
  transition: transform 0.25s;
  cursor: pointer;
  width: 220px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-subject--hidden {
  width: 220px;
  border: 1px solid transparent;
  padding: 4px 6px;
  border-radius: 4px;
  height: 30px;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  position: relative;
}

.td-subject--hidden:hover {
  border-color: var(--border-color);
}

.td-subject--hidden:hover:before {
  content: "\e90e";
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  position: absolute;
  top: 8px;
  right: 4px;
  opacity: 0.7;
}

.d-select-lookalike {
  height: 42px;
  position: relative;
}

.d-select-lookalike:after {
  content: 'Søg og vælg';
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  transition: width 0.25s;
  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 9px;
  background: #ffffff;
  cursor: text;
  border: 1px solid #acb6be;
}

.task__users {
  min-height: 22px;
  width: 100%;
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border 0.1s;
}

.task__users:hover {
  border: 1px solid #acb6be;
}

.task__users > p {
  display: inline-block;
  margin: 0;
}

.task__users > p:after {
  content: ',';
  margin-right: 5px;
}

.task__users > p:last-child:after {
  content: '';
}

.main-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #8898aa;
  font-style: italic;
  font-size: 14px;
}

.sidebar-empty {
  padding: 16px;
  font-style: italic;
  color: #8898aa;
  font-size: 13px;
}


.do-bttn--menu-button {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
  gap: 0.75rem;
  box-shadow: 0 0 rgba(0, 0, 0, 0), 0 0 rgba(0, 0, 0, 0), 0 1px 2px 0 rgba(0, 0, 0, .05);
}

.do-bttn--menu-button__list > li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.do-bttn--menu-button__list__print > li {
  gap: 0.5rem;
  justify-content: flex-start;
}

.bulk-action-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background-color: #eef6ff;
  border: 1px solid #bcdcff;
}

.bulk-action-bar__count {
  font-size: 13px;
  color: #334155;
  font-weight: 600;
}

.bulk-action-bar__label {
  font-size: 13px;
  color: #334155;
  font-weight: 600;
  margin: 0;
}

.bulk-action-bar__select {
  min-width: 180px;
}

.bulk-action-bar__input {
  width: 140px;
}

.bulk-action-bar__warning {
  font-size: 12px;
  color: #b45309;
  background-color: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 3px;
  padding: 4px 8px;
}

.bulk-action-bar__cancel {
  margin-left: auto;
}

.do-bttn.do-bttn--delete {
  background-color: #dc2626;
  border-color: #dc2626;
  color: #fff;
}

.do-bttn.do-bttn--delete:hover:not(:disabled) {
  background-color: #b91c1c;
  border-color: #b91c1c;
}

.do-bttn.do-bttn--delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.task-table-checkbox {
  text-align: center;
  vertical-align: middle;
  padding: 0;
}

.task-table-checkbox input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.do-bttn--active {
  background-color: #eef6ff;
  border-color: #bcdcff;
  color: #334155;
}

</style>



// WEBPACK FOOTER //
// SubjectsDistributionTasksFromSubjects.vue?60f9d40a